import { useQuery } from '@tanstack/react-query';
import type {
  CursorPage,
  Intent,
  Keyword,
  KeywordCluster,
  KeywordRun,
} from '@seo/types';
import { apiGet } from '@/lib/api';
import { isTerminal } from '@/lib/queryClient';

export function useKeywordRun(id: string | undefined) {
  return useQuery({
    queryKey: ['keyword-run', id],
    queryFn: () => apiGet<KeywordRun>(`/v1/keyword-runs/${id}`),
    enabled: !!id,
    refetchInterval: (q) => (isTerminal(q.state.data?.status) ? false : 5_000),
  });
}

export function useKeywordRunClusters(id: string | undefined) {
  return useQuery({
    queryKey: ['keyword-run-clusters', id],
    queryFn: () => apiGet<CursorPage<KeywordCluster>>(`/v1/keyword-runs/${id}/clusters`),
    enabled: !!id,
  });
}

/**
 * A2 v2 masterclass filters — the "elephant filter" query: max difficulty,
 * min volume, max SERP-DA, intent whitelist, and pillar-only view.
 */
export interface KeywordFilters {
  clusterId?: string;
  maxDifficulty?: number;
  minVolume?: number;
  maxSerpDa?: number;
  intent?: Intent[];
  pillarsOnly?: boolean;
}

export function useKeywordRunKeywords(
  id: string | undefined,
  filters?: KeywordFilters,
) {
  const params = new URLSearchParams();
  if (filters?.clusterId) params.set('cluster_id', filters.clusterId);
  if (filters?.maxDifficulty !== undefined)
    params.set('max_difficulty', String(filters.maxDifficulty));
  if (filters?.minVolume !== undefined)
    params.set('min_volume', String(filters.minVolume));
  if (filters?.maxSerpDa !== undefined)
    params.set('max_serp_da', String(filters.maxSerpDa));
  if (filters?.intent && filters.intent.length > 0)
    params.set('intent', filters.intent.join(','));
  if (filters?.pillarsOnly) params.set('pillars_only', 'true');
  const key = [
    'keyword-run-keywords',
    id,
    filters?.clusterId ?? null,
    filters?.maxDifficulty ?? null,
    filters?.minVolume ?? null,
    filters?.maxSerpDa ?? null,
    filters?.intent?.join(',') ?? null,
    filters?.pillarsOnly ?? null,
  ];
  return useQuery({
    queryKey: key,
    queryFn: () =>
      apiGet<CursorPage<Keyword>>(`/v1/keyword-runs/${id}/keywords?${params.toString()}`),
    enabled: !!id,
  });
}
